import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import { withTranslation } from "react-i18next";
import constants, {
  officePolicyAssignmentStatus,
  policyFolderSortOptions,
} from "../../../../../../../constants";
import { encodeId, formatCalendarDate, formatLocalDateTime } from "utils";
import moment from "moment";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Loader from "components/Loader";
import Empty from "components/Empty";
import Table from "components/table";
import usePolicyModuleListing from "../../../../hooks/usePolicyModuleListing";
import ComplianceDashboardFilters from "../../../../Filters/ComplianceDashboardFilters";
import SearchBar from "../../../../Filters/Searchbar";
import CustomDropdown from "components/Dropdown";
import checkIcon from "assets/images/ico-check.svg";
import sortIcon from "assets/images/mir-ico-sort.svg";
import { sortingVariablesForCurrentPolicies } from "../../../constants";
import InviteesPopup from "accountOwner/pages/EventDetails/components/Modals/InviteesPopup";
import { usePolicySelection } from "../../../../context/PolicySelectionContext";
import { shouldSkipComplianceFolderRowNavigation } from "../../../../hooks/useComplianceTableRowClick";
import {
  deletePolicyFolder,
  exportPolicyFolderList,
} from "repositories/office-policy-management-repository";
import toast from "react-hot-toast";
import { cacheSideBarActive } from "utils";
import styles from "../../../../OfficePolicyManagement.module.scss";
import "../../../../OfficePolicyManagement.scss";
import Text from "components/Text";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { getLocalTimeZoneCode } from "accountOwner/pages/AddEvent/addEvents.functions";
const OFFICE_DISPLAY_COUNT = 2;

// Show renotify only when not draft and there are assignees; hide when no assignees
const canShowRenotifyForRow = (row) => {
  if (!row) return false;
  if (row.isDraft ?? row.IsDraft) return false;
  const assignees = (row.assignees ?? row.Assignees ?? "").trim();
  return assignees !== "";
};

/**
 * Archived Policies tab listing.
 */
const ArchivedPolicies = ({ t, isActive = true }) => {
  const history = useHistory();
  const containerRef = useRef(null);
  const [isSidebarActive, setSidebarActive] = useState(
    cacheSideBarActive(constants.sidebar?.cache?.officePoliciesIsSidebarActive),
  );
  const [viewMoreOffices, setViewMoreOffices] = useState({
    isOpen: false,
    offices: [],
  });
  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    right: 0,
  });
  const { effectiveOwnerIdString } = useAccountOwnerCommon();
  const openRef = useRef(null);
  const suppressRowNavigationRef = useRef(false);
  const {
    isLoading,
    officePolicies,
    inputValue,
    handleSearchTerm,
    officeFilter,
    setOfficeFilter,
    staffFilter,
    setStaffFilter,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    categoryList,
    dateRange,
    setDateRange,
    isFilterApply,
    sortBy,
    setSortBy,
    isDescending,
    setIsDescending,
    dueDateFilter,
    setDueDateFilter,
    accountOwnerId,
    handleApplyFilters,
    resetFilter,
    handleArchivePolicyFolder,
    renotifyAction,
    renotifyBulkAction,
    isStaff,
    appliedOfficeFilters,
    appliedStaffFilters,
    appliedStatusFilters,
    appliedCategoryFilters,
    appliedDateRange,
    searchValue,
    isRenew,
  } = usePolicyModuleListing(true, isActive);

  const queryClient = useQueryClient();
  const [selectedFolderPolicyIds, setSelectedFolderPolicyIds] = useState([]);

  const {
    setSelectedFolderPolicyIds: setContextSelectedIds,
    setSelectedPolicyIds: setContextSelectedPolicyIds,
    setSelectedPolicyMeta,
    registerArchiveHandler,
    registerClearSelection,
    registerDeleteHandler,
    registerRenotifyHandler,
    registerExportHandler,
    openDeleteModal,
    setIsArchiveTab,
  } = usePolicySelection();

  const archiveHandlerRef = useRef(handleArchivePolicyFolder);
  archiveHandlerRef.current = handleArchivePolicyFolder;
  const exportHandlerRef = useRef(null);
  const prevHasAnyDeletableRef = useRef(null);

  // Invalidate only this tab's policy list when user switches here so list shows updated data (avoids calling both current + archived APIs)
  const prevActiveRef = useRef(isActive);
  useEffect(() => {
    if (isActive && !prevActiveRef.current) {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "/officePolicies/policyFolderList" &&
          query.queryKey[5] === true,
      });
    }
    prevActiveRef.current = isActive;
  }, [isActive, queryClient]);

  const prevHasAnyRenotifiableRef = useRef(null);
  const prevHasAnyRenewableRef = useRef(null);
  const prevPolicyIdsRef = useRef([]);
  const arraysEqual = (a, b) =>
    a.length === b.length && a.every((val, i) => val === b[i]);
  useEffect(() => {
    if (!isActive) return;
    const folderIds = selectedFolderPolicyIds || [];
    setContextSelectedIds(folderIds);
    const selectedPolicyIds = folderIds
      .map((id) => {
        const p = officePolicies?.find((x) => x.id === id);
        return p?.policyId ?? null;
      })
      .filter((id) => id != null);
    // Only update context when policy IDs actually changed to avoid infinite re-render (new array ref every run)
    if (!arraysEqual(prevPolicyIdsRef.current, selectedPolicyIds)) {
      prevPolicyIdsRef.current = selectedPolicyIds;
      setContextSelectedPolicyIds(selectedPolicyIds);
    }
    const hasAnyDeletable = Boolean(
      selectedFolderPolicyIds?.length > 0 &&
      selectedFolderPolicyIds.some((id) => {
        const p = officePolicies?.find((x) => x.id === id);
        return p && (p.isDeletable ?? p.IsDeletable);
      }),
    );
    const hasAnyRenotifiable = Boolean(
      selectedFolderPolicyIds?.length > 0 &&
      selectedFolderPolicyIds.some((id) => {
        const p = officePolicies?.find((x) => x.id === id);
        return canShowRenotifyForRow(p);
      }),
    );
    const hasAnyRenewable = Boolean(
      selectedFolderPolicyIds?.length > 0 &&
      selectedFolderPolicyIds.some((id) => {
        const p = officePolicies?.find((x) => x.id === id);
        return canShowRenotifyForRow(p);
      }),
    );
    if (
      prevHasAnyDeletableRef.current !== hasAnyDeletable ||
      prevHasAnyRenotifiableRef.current !== hasAnyRenotifiable ||
      prevHasAnyRenewableRef.current !== hasAnyRenewable
    ) {
      prevHasAnyDeletableRef.current = hasAnyDeletable;
      prevHasAnyRenotifiableRef.current = hasAnyRenotifiable;
      prevHasAnyRenewableRef.current = hasAnyRenewable;
      setSelectedPolicyMeta({
        hasAnyDeletable,
        hasAnyRenotifiable,
        hasAnyRenewable,
      });
    }
  }, [
    isActive,
    selectedFolderPolicyIds,
    officePolicies,
    setContextSelectedIds,
    setContextSelectedPolicyIds,
    setSelectedPolicyMeta,
  ]);

  // Register unarchive, delete, renotify, and export handlers when this tab is active
  useEffect(() => {
    if (!isActive) return;
    setIsArchiveTab(true);
    registerArchiveHandler((ids) => archiveHandlerRef.current?.(ids, false));
    registerClearSelection(() => setSelectedFolderPolicyIds([]));
    registerRenotifyHandler((ids) => renotifyBulkAction(ids));
    registerExportHandler(() => exportHandlerRef.current?.());
    registerDeleteHandler(async (ids) => {
      const policies = officePolicies || [];
      const deletableIds = ids.filter((id) => {
        const p = policies.find((x) => x.id === id);
        return p && (p.isDeletable ?? p.IsDeletable);
      });
      if (deletableIds.length === 0) return;
      try {
        const res = await deletePolicyFolder({
          OwnerId: accountOwnerId,
          Items: deletableIds.map((id) => {
            const p = policies.find((x) => x.id === id);
            return {
              PolicyFolderId: id,
              PolicyId: p?.policyId ?? null,
            };
          }),
        });
        if (res?.message) toast.success(res.message);
        queryClient.invalidateQueries({
          predicate: (q) =>
            q.queryKey[0] === "/officePolicies/policyFolderList" &&
            q.queryKey[5] === true,
        });
      } catch (e) {
        toast.error(e?.message || "Failed to delete policy");
      }
    });
    return () => {
      registerClearSelection(null);
      registerDeleteHandler(null);
      registerRenotifyHandler(null);
      registerExportHandler(null);
    };
  }, [
    isActive,
    registerArchiveHandler,
    registerClearSelection,
    registerDeleteHandler,
    registerRenotifyHandler,
    registerExportHandler,
    renotifyBulkAction,
    setIsArchiveTab,
    officePolicies,
    accountOwnerId,
    queryClient,
  ]);

  const handleSortChange = useCallback(
    (value) => {
      setDueDateFilter(Number(value));
    },
    [setDueDateFilter],
  );

  const handleExport = useCallback(async () => {
    if (!accountOwnerId) return;
    const { timeZoneId } = getLocalTimeZoneCode();
    const fromDateParam = appliedDateRange?.fromDate || null;
    const toDateParam = appliedDateRange?.toDate || null;
    const startDateStr =
      fromDateParam &&
      (fromDateParam instanceof Date
        ? moment(fromDateParam).format("YYYY-MM-DD")
        : fromDateParam);
    const endDateStr =
      toDateParam &&
      (toDateParam instanceof Date
        ? moment(toDateParam).format("YYYY-MM-DD")
        : toDateParam);
    try {
      await exportPolicyFolderList(accountOwnerId, timeZoneId, {
        appliedOfficeFilters: appliedOfficeFilters || [],
        appliedUserIds: appliedStaffFilters || [],
        searchValue: searchValue || "",
        isArchive: true,
        appliedStatusFilters: appliedStatusFilters || [],
        appliedCategoryFilters: appliedCategoryFilters || [],
        startDate: startDateStr || null,
        endDate: endDateStr || null,
        sortBy,
        sortDirection: isDescending ? 2 : 1,
        DueDateFilter: dueDateFilter,
        IsRenew: isRenew ?? false,
      });
    } catch (err) {
      toast.error(err?.message || t("officePolicyManagement.exportFailed"));
    }
  }, [
    accountOwnerId,
    appliedOfficeFilters,
    appliedStaffFilters,
    appliedStatusFilters,
    appliedCategoryFilters,
    appliedDateRange,
    searchValue,
    sortBy,
    isDescending,
    dueDateFilter,
    isRenew,
    t,
  ]);

  useEffect(() => {
    exportHandlerRef.current = handleExport;
    return () => {
      exportHandlerRef.current = null;
    };
  }, [handleExport]);

  const sortAndAscDescSetter = useCallback(
    (assignSort) => {
      let newIsDescending = true;
      if (sortBy === assignSort) {
        newIsDescending = !isDescending;
      }
      setSortBy(assignSort);
      setIsDescending(newIsDescending);
    },
    [sortBy, isDescending, setSortBy, setIsDescending],
  );

  const handleSidebarToggle = () => {
    const next = !isSidebarActive;
    setSidebarActive(next);
    if (constants.sidebar?.cache?.officePoliciesIsSidebarActive) {
      localStorage.setItem(
        constants.sidebar.cache.officePoliciesIsSidebarActive,
        next,
      );
    }
  };

  const handleViewMoreOffices = useCallback((e, offices) => {
    e.stopPropagation();

    let top = 40;
    let right = 40;

    if (containerRef.current && e.currentTarget) {
      const btnRect = e.currentTarget.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      const popupEstimatedHeight = 250;
      const popupEstimatedWidth = 280;

      // Calculate relative to the `.position-relative` container
      top = btnRect.bottom - containerRect.top + 5;

      // Compute 'right' properly so it aligns directly with the button element
      right = containerRect.right - btnRect.right;

      // Vertical boundary: If it overflows the screen, pop it up above the button
      if (
        btnRect.bottom + popupEstimatedHeight > window.innerHeight &&
        btnRect.top > popupEstimatedHeight
      ) {
        top = btnRect.top - containerRect.top - popupEstimatedHeight - 5;
      }

      // Horizontal boundary: Ensure it doesn't push past the left bound of the container
      if (right + popupEstimatedWidth > containerRect.width) {
        right = Math.max(10, containerRect.width - popupEstimatedWidth - 10);
      }

      if (right < 0) {
        right = 10;
      }
    }

    setInvitePopupPosition({ top, right });
    setViewMoreOffices({
      isOpen: true,
      offices: Array.isArray(offices) ? offices : [],
    });
  }, []);

  const handleInvitePopupToggle = useCallback((e) => {
    e?.stopPropagation?.();
    setViewMoreOffices((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const officesDisplay = useMemo(() => {
    return (policy) => {
      const raw = policy?.offices ?? policy?.officeList ?? [];
      let names = [];
      if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) {
          return (
            <span className={`${styles["table-text"]}`}>
              {policy?.isAllOffice ? t("notes.allFilters") : "—"}
            </span>
          );
        }
        names = trimmed.split(/\s*,\s*/).filter(Boolean);
      } else {
        const list = Array.isArray(raw) ? raw : [];
        names = list
          .map((o) =>
            typeof o === "string" ? o : (o?.name ?? o?.officeName ?? ""),
          )
          .filter(Boolean);
      }
      const total = names.length;
      if (total === 0) {
        return (
          <span className={`${styles["table-text"]}`}>
            {policy?.isAllOffice ? t("notes.allFilters") : "—"}
          </span>
        );
      }
      if (total <= OFFICE_DISPLAY_COUNT) {
        return (
          <span className={`${styles["table-text"]}`}>{names.join(", ")}</span>
        );
      }
      const first = names.slice(0, OFFICE_DISPLAY_COUNT).join(", ");
      let allOfficeObjects;
      if (typeof raw === "string") {
        allOfficeObjects = names.map((name, i) => ({
          name,
          officeName: name,
          id: `office-${i}`,
        }));
      } else {
        const list = Array.isArray(raw) ? raw : [];
        allOfficeObjects = list;
      }
      return (
        <>
          <span className={`${styles["table-text"]}`}>{first}, </span>
          <button
            type="button"
            className="link-btn p-0 border-0 bg-transparent text-primary"
            onClick={(e) => handleViewMoreOffices(e, allOfficeObjects)}
          >
            +{total - OFFICE_DISPLAY_COUNT} {t("officePolicyManagement.more")}
          </button>
        </>
      );
    };
  }, [t, handleViewMoreOffices]);

  const handleEdit = useCallback(
    (policyId) => {
      history.push({
        pathname:
          constants.routes.officePolicyManagements.officePolicyManagementEditPolicyFolder.replace(
            ":officePolicyId",
            encodeId(policyId),
          ),
        search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
      });
    },
    [history, effectiveOwnerIdString],
  );

  const handleDeleteRow = useCallback(
    (row) => {
      const isDeletable = row?.isDeletable ?? row?.IsDeletable ?? false;
      if (isDeletable && row?.id != null) openDeleteModal([row.id]);
    },
    [openDeleteModal],
  );

  const handleViewDetails = useCallback(
    (officePolicyFolderId) => {
      const route =
        constants.routes.officePolicyManagements.policyDocumentListing;
      history.push({
        pathname: route.replace(
          ":officePolicyFolderId",
          encodeId(officePolicyFolderId),
        ),
        search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
      });
    },
    [history, effectiveOwnerIdString],
  );

  const preventRowNavigation = useCallback((e) => {
    e?.stopPropagation?.();
    suppressRowNavigationRef.current = true;
    window.setTimeout(() => {
      suppressRowNavigationRef.current = false;
    }, 300);
  }, []);

  const runDropdownAction = useCallback(
    (e, action) => {
      preventRowNavigation(e);
      action?.();
    },
    [preventRowNavigation],
  );

  const complianceRowEvents = useMemo(
    () => ({
      onClick: (e, row) => {
        if (suppressRowNavigationRef.current) return;
        if (shouldSkipComplianceFolderRowNavigation(e)) return;
        handleViewDetails(row?.id);
      },
    }),
    [handleViewDetails],
  );

  const getStatusProgress = useCallback(
    (policy) => {
      const percentFromApi = policy?.assignmentCompletedPercent;
      const percent =
        typeof percentFromApi === "number" ? Math.round(percentFromApi) : 0;
      const statuses = {
        [officePolicyAssignmentStatus.IN_PROGRESS]: "In Progress",
        [officePolicyAssignmentStatus.PENDING]: "Pending",
        [officePolicyAssignmentStatus.ACCEPTED]: "Accepted",
        [officePolicyAssignmentStatus.STARTED]: "Just Started",
        [officePolicyAssignmentStatus.COMPLETED]: "Completed",
        [officePolicyAssignmentStatus.REJECTED]: "Rejected",
        [officePolicyAssignmentStatus.CANCELLED]: "Cancelled",
        [officePolicyAssignmentStatus.DRAFT]: "Draft",
      };
      if (policy?.assignees?.length === 0) {
        return {
          percent: 0,
          label: t("officePolicyManagement.pendingNotAssigned"),
        };
      }
      return { percent, label: statuses[policy?.policyStatus] || "—" };
    },
    [t],
  );

  const tableDataWithSelectionKey = useMemo(
    () => (officePolicies || []).map((row) => ({ ...row })),
    [officePolicies],
  );

  const handleSelectAll = useCallback(
    (e) => {
      const checked = e.target.checked;
      const nextIds = checked ? (officePolicies?.map((p) => p.id) ?? []) : [];
      setSelectedFolderPolicyIds(nextIds);
      setContextSelectedIds(nextIds);
    },
    [officePolicies, setContextSelectedIds],
  );

  const handleSelectOne = useCallback(
    (e, row) => {
      const id =
        row?.id !== undefined ? row.id : Number.parseInt(e.target.value, 10);
      if (e.target.checked) {
        setSelectedFolderPolicyIds((prev) => {
          const next = prev.includes(id) ? prev : [...prev, id];
          setContextSelectedIds(next);
          return next;
        });
      } else {
        setSelectedFolderPolicyIds((prev) => {
          const next = prev.filter((it) => it !== id);
          setContextSelectedIds(next);
          return next;
        });
      }
    },
    [setContextSelectedIds],
  );

  const columns = useMemo(
    () => [
      {
        attrs: { datatitle: "" },
        dataField: "select",
        text: "",
        headerFormatter: () => {
          const allSelected =
            officePolicies?.length > 0 &&
            selectedFolderPolicyIds?.length === officePolicies?.length;
          return (
            <div className={`ch-checkbox ${styles["select-all-checkbox"]}`}>
              <label className="mb-0">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
                <span className="text-decoration-none">&nbsp;</span>
              </label>
            </div>
          );
        },
        formatter: (cell, row) => (
          <div
            className="ch-checkbox"
            onMouseDown={preventRowNavigation}
            onClick={preventRowNavigation}
          >
            <label className="mb-0">
              <input
                type="checkbox"
                aria-label="Select policy"
                value={row.id}
                checked={selectedFolderPolicyIds?.includes(row.id)}
                onChange={(e) => handleSelectOne(e, row)}
              />
              <span className="text-decoration-none">&nbsp;</span>
            </label>
          </div>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.categoryName") },
        dataField: "policyCategoryName",
        text: t("officePolicyManagement.categoryName"),
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className="reset-btn flex-shrink-null"
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForCurrentPolicies.Category,
                  );
                }}
              >
                <span>{t("officePolicyManagement.categoryName")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy === sortingVariablesForCurrentPolicies.Category
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={`${styles["table-text"]}`}>
            {row?.categoryName ??
              row?.policyCategoryName ??
              row?.category?.title ??
              "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.policyName") },
        dataField: "title",
        text: t("officePolicyManagement.policyName"),
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className="reset-btn flex-shrink-null"
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForCurrentPolicies.Title,
                  );
                }}
              >
                <span>{t("officePolicyManagement.policyName")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy === sortingVariablesForCurrentPolicies.Title
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={`${styles["table-text"]}`}>
            {(row?.title && String(row.title).trim()) || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.office") },
        dataField: "office",
        text: t("officePolicyManagement.office"),
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1 text-capitalize">
              <span>{t("officePolicyManagement.office")}</span>
            </div>
          );
        },
        formatter: (cell, row) => officesDisplay(row),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.startDate") },
        dataField: "startDate",
        text: t("officePolicyManagement.startDate"),
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className="reset-btn flex-shrink-null"
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForCurrentPolicies.StartDate,
                  );
                }}
              >
                <span>{t("officePolicyManagement.startDate")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy === sortingVariablesForCurrentPolicies.StartDate
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={`${styles["table-text"]}`}>
            {formatCalendarDate(
              row?.startDate ?? row?.createdAt,
              "MMM DD, YYYY",
            ) || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.dueDate") },
        dataField: "dueDate",
        text: t("officePolicyManagement.dueDate"),
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className="reset-btn flex-shrink-null"
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForCurrentPolicies.DueDate,
                  );
                }}
              >
                <span>{t("officePolicyManagement.dueDate")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy === sortingVariablesForCurrentPolicies.DueDate
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={`${styles["table-text"]}`}>
            {formatCalendarDate(row?.dueDate, "MMM DD, YYYY") || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.lastUpdatedOn") },
        dataField: "lastUpdatedOn",
        text: t("officePolicyManagement.lastUpdatedOn"),
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className="reset-btn flex-shrink-null"
                onClick={() => {
                  sortAndAscDescSetter(
                    sortingVariablesForCurrentPolicies.LastUpdatedOn,
                  );
                }}
              >
                <span>{t("officePolicyManagement.lastUpdatedOn")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending &&
                      sortBy ===
                        sortingVariablesForCurrentPolicies.LastUpdatedOn
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cell, row) => (
          <span className={`${styles["table-text"]}`}>
            {formatLocalDateTime(
              row?.lastUpdatedOn ?? row?.updatedAt ?? row?.modifiedAt,
              "MMM DD, YYYY",
            ) || "—"}
          </span>
        ),
        sort: false,
      },
      {
        attrs: { datatitle: t("notes.status") },
        dataField: "status",
        text: t("notes.status"),
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1 text-capitalize">
              <span>{t("notes.status")}</span>
            </div>
          );
        },
        formatter: (cell, row) => {
          const progress = getStatusProgress(row);
          const isJustStarted =
            row?.policyStatus === officePolicyAssignmentStatus.STARTED;
          return (
            <div className="d-flex flex-column align-items-start gap-1">
              <div
                className={
                  styles.status_progress_track ??
                  "rounded overflow-hidden bg-light"
                }
                style={{
                  height: 8,
                  minWidth: 60,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {isJustStarted ? (
                  <span
                    className={` rounded-circle flex-shrink-0  ${styles["bg-green-progress"]} `}
                    style={{ width: 8, height: 8, minWidth: 8 }}
                  />
                ) : (
                  <div
                    className={` ${styles["bg-green-progress"]} `}
                    style={{
                      height: "100%",
                      width: `${Math.min(100, progress.percent)}%`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {progress.percent > 0 && progress.percent < 100 && (
                      <div
                        style={{
                          width: 4,
                          height: 4,
                          backgroundColor: "#fff",
                          borderRadius: "50%",
                          marginRight: 2,
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
              <span className={` ${styles["progress-text"]} `}>
                {progress.label}
              </span>
            </div>
          );
        },
        sort: false,
      },
      {
        attrs: { datatitle: t("officePolicyManagement.actions") },
        dataField: "actions",
        text: t("officePolicyManagement.actions"),
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1 text-capitalize">
              <span>{t("officePolicyManagement.actions")}</span>
            </div>
          );
        },
        formatter: (cell, row) => (
          <div
            className="actions-status-box"
            onMouseDown={preventRowNavigation}
            onClick={preventRowNavigation}
          >
            <UncontrolledDropdown
              tag="div"
              className={`interactive-element d-inline-block ${styles.policy_row_actions_dropdown}`}
            >
              <DropdownToggle
                tag="div"
                className="cursor-pointer p-1"
                caret={false}
                onMouseDown={preventRowNavigation}
                onClick={preventRowNavigation}
              >
                <img
                  src={require("assets/images/dots-icon.svg").default}
                  alt="actions"
                />
              </DropdownToggle>
              <DropdownMenu
                end
                container={openRef}
                onMouseDown={preventRowNavigation}
              >
                <DropdownItem
                  onMouseDown={preventRowNavigation}
                  onClick={(e) =>
                    runDropdownAction(e, () => handleEdit(row?.id))
                  }
                >
                  <Text className={"office-dropdown-item"}>
                    {t("officePolicyManagement.editPolicy")}
                  </Text>
                </DropdownItem>
                {(row?.isDeletable ?? row?.IsDeletable) && (
                  <DropdownItem
                    onMouseDown={preventRowNavigation}
                    onClick={(e) =>
                      runDropdownAction(e, () => handleDeleteRow(row))
                    }
                  >
                    <Text className={"office-dropdown-item"}>
                      {t("officePolicyManagement.deletePolicy")}
                    </Text>
                  </DropdownItem>
                )}
                <DropdownItem
                  onMouseDown={preventRowNavigation}
                  onClick={(e) =>
                    runDropdownAction(e, () =>
                      handleArchivePolicyFolder(row.id, false),
                    )
                  }
                >
                  <Text className={"office-dropdown-item"}>
                    {t("officePolicyManagement.unarchivePolicy")}
                  </Text>
                </DropdownItem>
                <DropdownItem
                  onMouseDown={preventRowNavigation}
                  onClick={(e) =>
                    runDropdownAction(e, () => handleViewDetails(row?.id))
                  }
                >
                  <Text className={"office-dropdown-item"}>
                    {t("officePolicyManagement.viewDetails")}
                  </Text>
                </DropdownItem>
                {canShowRenotifyForRow(row) && (
                  <DropdownItem
                    onMouseDown={preventRowNavigation}
                    onClick={(e) =>
                      runDropdownAction(e, () =>
                        renotifyAction(row?.policyId ?? row.id),
                      )
                    }
                  >
                    <Text className={"office-dropdown-item"}>
                      {t("officePolicyManagement.renotify")}
                    </Text>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        ),
        sort: false,
      },
    ],
    [
      t,
      officePolicies,
      selectedFolderPolicyIds,
      officesDisplay,
      getStatusProgress,
      handleSelectAll,
      handleSelectOne,
      handleEdit,
      handleDeleteRow,
      handleArchivePolicyFolder,
      handleViewDetails,
      renotifyAction,
      sortAndAscDescSetter,
      sortBy,
      isDescending,
      preventRowNavigation,
      runDropdownAction,
    ],
  );

  const dataToShow = tableDataWithSelectionKey;

  return (
    <div className="pt-0 position-relative" ref={containerRef}>
      <div className={styles.wrapper_card}>
        <div className="scheduler-tabs-main-wrapper">
          <ComplianceDashboardFilters
            t={t}
            isSidebarActive={isSidebarActive}
            handleSidebarToggle={handleSidebarToggle}
            resetFilter={resetFilter}
            officeFilter={officeFilter}
            setOfficeFilter={setOfficeFilter}
            handleApplyFilters={handleApplyFilters}
            staffFilter={staffFilter}
            setStaffFilter={setStaffFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            categoryList={categoryList}
            dateRange={dateRange}
            setDateRange={setDateRange}
            isFilterApply={isFilterApply}
            isStaff={isStaff}
            accountOwnerId={accountOwnerId}
          />

          <div className={styles.compliance_table_wrapper}>
            <div className={styles.compliance_table_toolbar}>
              <SearchBar
                searchValue={inputValue}
                handleSearchTerm={handleSearchTerm}
                t={t}
                placeholder={
                  t("officePolicyManagement.searchByPolicyName") ||
                  "Search by Policy Name"
                }
              />
              <div className={styles.compliance_sort_dropdown_wrapper}>
                <CustomDropdown
                  options={policyFolderSortOptions}
                  selectedOption={dueDateFilter}
                  selectOption={handleSortChange}
                  renderAnyThing={(id) =>
                    dueDateFilter === id ? (
                      <img src={checkIcon} alt="" className="mr-2" />
                    ) : (
                      <span className="d-inline me-2" style={{ width: 22 }} />
                    )
                  }
                  matchWithOther={[policyFolderSortOptions[0].id]}
                />
              </div>
            </div>
            {isLoading && <Loader />}
            {!isLoading && (!officePolicies || officePolicies.length === 0) && (
              <Empty
                Message={t(
                  "officePolicyManagement.noArchivedPolicyAvailableForSelection",
                )}
              />
            )}
            {!isLoading && officePolicies?.length > 0 && (
              <div className={styles.compliance_table_scroll}>
                <div
                  className="actions-status-box"
                  ref={openRef}
                  onMouseDown={preventRowNavigation}
                  onClick={preventRowNavigation}
                />
                <Table
                  key={`compliance-table-${selectedFolderPolicyIds.join("-")}`}
                  keyField="id"
                  data={dataToShow}
                  columns={columns}
                  bordered={false}
                  disablePagination
                  tableClassName={`${styles.compliance_policy_table} ${styles.compliance_policy_folder_table}`}
                  rowEvents={complianceRowEvents}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {viewMoreOffices.isOpen && viewMoreOffices.offices.length > 0 && (
        <InviteesPopup
          position={invitePopupPosition}
          handleInvitePopupToggle={handleInvitePopupToggle}
          list={viewMoreOffices.offices}
          onlyOfffice={true}
          isForEvent={true}
          isfromBusySlot={true}
          listClass={styles.compliance_invitees_popup_list}
        />
      )}
    </div>
  );
};

export default withTranslation()(ArchivedPolicies);
